/**
 * Dusk N Dawn — Project Origins
 * Phase 2.3 complete motion controller
 */

(() => {
  'use strict';

  const SELECTORS = {
    page: '.dnd-origins',
    hero: '.dnd-origins-hero',
    heroImage: '.dnd-origins-hero__image',
    reveal: '.dnd-reveal'
  };

  const CLASSES = {
    ready: 'is-ready',
    visible: 'is-visible',
    scrolled: 'dnd-origins-scrolled',
    pageActive: 'dnd-origins-page-active',
    motionReady: 'dnd-motion-ready'
  };

  const SETTINGS = {
    revealThreshold: 0.12,
    revealRootMargin: '0px 0px -60px 0px',
    parallaxMinWidth: 990,
    parallaxMaxOffset: 70,
    parallaxEase: 0.075,
    scrollStateOffset: 20
  };

  let revealObserver = null;
  let hero = null;
  let heroImage = null;
  let progressElement = null;
  let sectionObserver = null;
  let activeSection = null;

  let targetParallax = 0;
  let currentParallax = 0;
  let animationFrame = null;
  let animationRunning = false;
  let pageIsVisible = true;
  let heroIsNearViewport = true;
  let prefersReducedMotion = false;
  let parallaxEnabled = false;

  const observedElements = new WeakSet();

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function lerp(start, end, amount) {
    return start + (end - start) * amount;
  }

  function updateMotionPreference() {
    prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
  }

  function getOriginsPage() {
    return (
      document.querySelector(SELECTORS.page) ||
      document.querySelector(SELECTORS.hero)?.closest('main') ||
      document.querySelector(SELECTORS.reveal)?.closest('main') ||
      document.querySelector('#MainContent')
    );
  }

  function createProgressIndicator() {
    progressElement = document.querySelector(
      '.dnd-origins-progress'
    );

    if (progressElement) return;

    progressElement = document.createElement('div');
    progressElement.className = 'dnd-origins-progress';
    progressElement.setAttribute('aria-hidden', 'true');

    document.body.appendChild(progressElement);
  }

  function updateScrollProgress() {
    if (!progressElement) return;

    const page = getOriginsPage();

    if (!page) {
      document.body.classList.remove(
        CLASSES.pageActive
      );

      return;
    }

    const pageRect = page.getBoundingClientRect();
    const pageTop = window.scrollY + pageRect.top;
    const pageHeight = page.offsetHeight;
    const viewportHeight = window.innerHeight;

    const scrollableDistance = Math.max(
      pageHeight - viewportHeight,
      1
    );

    const travelledDistance =
      window.scrollY - pageTop;

    const progress = clamp(
      travelledDistance / scrollableDistance,
      0,
      1
    );

    progressElement.style.setProperty(
      '--dnd-progress',
      progress.toFixed(4)
    );

    const pageBottom = pageTop + pageHeight;

    const pageIsVisible =
      window.scrollY + viewportHeight > pageTop &&
      window.scrollY < pageBottom;

    document.body.classList.toggle(
      CLASSES.pageActive,
      pageIsVisible
    );
  }

  function showElement(element) {
    if (!element) return;

    element.classList.add(CLASSES.visible);

    if (revealObserver) {
      revealObserver.unobserve(element);
    }
  }

  function createRevealObserver() {
    if (
      prefersReducedMotion ||
      !('IntersectionObserver' in window)
    ) {
      return;
    }

    revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add(
            CLASSES.visible
          );

          observer.unobserve(entry.target);
        });
      },
      {
        threshold: SETTINGS.revealThreshold,
        rootMargin: SETTINGS.revealRootMargin
      }
    );
  }

  function registerRevealElements(root = document) {
    if (!root?.querySelectorAll) return;

    root.querySelectorAll(SELECTORS.reveal)
      .forEach((element, index) => {
        if (
          !element.style.getPropertyValue(
            '--dnd-reveal-delay'
          )
        ) {
          element.style.setProperty(
            '--dnd-reveal-delay',
            `${(index % 6) * 70}ms`
          );
        }

        if (!revealObserver) {
          showElement(element);
          return;
        }

        if (observedElements.has(element)) return;

        observedElements.add(element);
        revealObserver.observe(element);
      });
  }

  function updateParallaxState() {
    parallaxEnabled =
      Boolean(heroImage) &&
      !prefersReducedMotion &&
      window.innerWidth >=
        SETTINGS.parallaxMinWidth;

    if (!parallaxEnabled && heroImage) {
      heroImage.style.removeProperty(
        '--dnd-parallax-y'
      );
    }
  }

  function calculateParallaxTarget() {
    if (!parallaxEnabled || !hero) return;

    const heroRect = hero.getBoundingClientRect();
    const heroHeight = Math.max(
      heroRect.height,
      1
    );

    const progress = clamp(
      -heroRect.top / heroHeight,
      0,
      1
    );

    targetParallax =
      progress * SETTINGS.parallaxMaxOffset;
  }

  function animateParallax() {
  if (
    !parallaxEnabled ||
    !heroImage ||
    !pageIsVisible ||
    !heroIsNearViewport
  ) {
    animationRunning = false;
    animationFrame = null;
    return;
  }

    currentParallax = lerp(
      currentParallax,
      targetParallax,
      SETTINGS.parallaxEase
    );

    heroImage.style.setProperty(
      '--dnd-parallax-y',
      `${currentParallax.toFixed(2)}px`
    );

    if (
      Math.abs(
        targetParallax - currentParallax
      ) > 0.05
    ) {
      animationFrame =
        requestAnimationFrame(
          animateParallax
        );
    } else {
      animationRunning = false;
      animationFrame = null;
    }
  }

 function requestParallaxAnimation() {
  if (
    animationRunning ||
    !parallaxEnabled ||
    !heroImage ||
    !pageIsVisible ||
    !heroIsNearViewport
  ) {
    return;
  }

  animationRunning = true;

  animationFrame =
    window.requestAnimationFrame(
      animateParallax
    );
}

  /**
 * Check whether the hero is close enough to the viewport
 * for parallax calculations to be useful.
 */
function updateHeroViewportState() {
  if (!hero) {
    heroIsNearViewport = false;
    return;
  }

  const rect = hero.getBoundingClientRect();
  const viewportHeight = window.innerHeight;

  heroIsNearViewport =
    rect.bottom > -200 &&
    rect.top < viewportHeight + 200;
}

  function handleScroll() {
  document.body.classList.toggle(
    CLASSES.scrolled,
    window.scrollY >
      SETTINGS.scrollStateOffset
  );

  updateScrollProgress();
  updateHeroViewportState();

  if (
    !parallaxEnabled ||
    !heroIsNearViewport
  ) {
    return;
  }

  calculateParallaxTarget();
  requestParallaxAnimation();
}

  function handleResize() {
  updateMotionPreference();
  updateParallaxState();
  updateHeroViewportState();
  updateScrollProgress();

  if (!parallaxEnabled) {
    targetParallax = 0;
    currentParallax = 0;

    if (animationFrame) {
      window.cancelAnimationFrame(
        animationFrame
      );
    }

    animationFrame = null;
    animationRunning = false;

    if (heroImage) {
      heroImage.style.removeProperty(
        '--dnd-parallax-y'
      );
    }

    return;
  }

  calculateParallaxTarget();
  requestParallaxAnimation();
}

  function prepareHero() {
    hero = document.querySelector(
      SELECTORS.hero
    );

    heroImage = document.querySelector(
      SELECTORS.heroImage
    );

    if (!hero) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        hero.classList.add(CLASSES.ready);

        document.documentElement.classList.add(
          CLASSES.motionReady
        );
      });
    });
  }

  function handleSectionLoad(event) {
    registerImages(event.target);
    registerRevealElements(event.target);

    hero = document.querySelector(
      SELECTORS.hero
    );

    heroImage = document.querySelector(
      SELECTORS.heroImage
    );
    

    updateParallaxState();
    updateScrollProgress();

    if (sectionObserver) {
  sectionObserver.disconnect();
  sectionObserver = null;
  }

    createSectionObserver();
  }

  /**
 * Set the section currently receiving visual focus.
 */
function setActiveSection(section) {
  if (!section) return;

  if (
    activeSection &&
    activeSection !== section
  ) {
    activeSection.classList.remove(
      'dnd-section-active'
    );
  }

  activeSection = section;

  activeSection.classList.add(
    'dnd-section-active'
  );
}


/**
 * Register Project Origins sections for focus tracking.
 */
function createSectionObserver() {
  const page =
    document.querySelector(SELECTORS.page);

  if (!page) return;

  const sections =
    page.querySelectorAll('section');

  if (!sections.length) return;

  /*
   * Reduced-motion users and older browsers receive
   * the normal page without section focus animation.
   */
  if (
    prefersReducedMotion ||
    !('IntersectionObserver' in window)
  ) {
    sections.forEach((section) => {
      section.classList.add(
        'dnd-section-active'
      );
    });

    return;
  }

  sectionObserver =
    new IntersectionObserver(
      (entries) => {
        const visibleSections =
          entries
            .filter((entry) => {
              return entry.isIntersecting;
            })
            .sort((first, second) => {
              return (
                second.intersectionRatio -
                first.intersectionRatio
              );
            });

        if (!visibleSections.length) return;

        setActiveSection(
          visibleSections[0].target
        );
      },
      {
        threshold: [
          0.15,
          0.3,
          0.45,
          0.6,
          0.75
        ],

        rootMargin:
          '-18% 0px -32% 0px'
      }
    );

  sections.forEach((section) => {
    sectionObserver.observe(section);
  });

  page.classList.add(
    'dnd-section-focus-ready'
  );

  setActiveSection(sections[0]);
}

/**
 * Mark one Project Origins image as loaded.
 */
function markImageLoaded(image) {
  if (!image) return;

  image.classList.remove(
    'dnd-image-loading'
  );

  image.classList.add(
    'dnd-image-loaded'
  );
}


/**
 * Mark one image as failed without leaving it hidden.
 */
function markImageError(image) {
  if (!image) return;

  image.classList.remove(
    'dnd-image-loading'
  );

  image.classList.add(
    'dnd-image-loaded',
    'dnd-image-error'
  );
}


/**
 * Register Project Origins images for premium loading.
 */
function registerImages(root = document) {
  if (!root?.querySelectorAll) return;

  const images =
    root.querySelectorAll(
      '.dnd-origins img'
    );

  images.forEach((image) => {
    if (
      image.dataset.dndImageRegistered ===
      'true'
    ) {
      return;
    }

    image.dataset.dndImageRegistered =
      'true';

    image.classList.add(
      'dnd-image-loading'
    );

    if (
      image.complete &&
      image.naturalWidth > 0
    ) {
      requestAnimationFrame(() => {
        markImageLoaded(image);
      });

      return;
    }

    image.addEventListener(
      'load',
      () => markImageLoaded(image),
      { once: true }
    );

    image.addEventListener(
      'error',
      () => markImageError(image),
      { once: true }
    );
  });
}

/**
 * Pause motion when the browser tab is hidden.
 */
function handleVisibilityChange() {
  pageIsVisible =
    document.visibilityState === 'visible';

  if (!pageIsVisible) {
    if (animationFrame) {
      window.cancelAnimationFrame(
        animationFrame
      );
    }

    animationFrame = null;
    animationRunning = false;
    return;
  }

  updateHeroViewportState();
  calculateParallaxTarget();
  requestParallaxAnimation();
}

  function bindEvents() {
    window.addEventListener(
      'scroll',
      handleScroll,
      { passive: true }
    );

    window.addEventListener(
      'resize',
      handleResize,
      { passive: true }
    );

    document.addEventListener(
  'visibilitychange',
  handleVisibilityChange
);

    document.addEventListener(
      'shopify:section:load',
      handleSectionLoad
    );

    document.addEventListener(
      'shopify:section:reorder',
      handleSectionLoad
    );

    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const element =
          event.target.closest(
            SELECTORS.reveal
          );

        if (element) {
          showElement(element);
        }
      }
    );
  }

  function initialise() {
    const originsContent =
      document.querySelector(
        SELECTORS.page
      ) ||
      document.querySelector(
        SELECTORS.hero
      ) ||
      document.querySelector(
        SELECTORS.reveal
      );

    if (!originsContent) return;

    updateMotionPreference();
    registerImages();
    prepareHero();
    createRevealObserver();
    registerRevealElements();
    updateParallaxState();
    createProgressIndicator();
    createSectionObserver();
    bindEvents();
    updateHeroViewportState();
    handleScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialise,
      { once: true }
    );
  } else {
    initialise();
  }
})();